import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import projectService from '../../services/projectService';
import type { Project } from '../../types/projects';
import { ProjectStatusPill, PublishedBadge } from '../Projects/ProjectBadges';
import { authorName, byLastActivity, lastActivity, timeAgo } from '../Projects/projectFormat';

const LIMIT = 5;

const publishedTime = (p: Project): number => (p.published_at ? Date.parse(p.published_at) || 0 : 0);

interface ProjectsWidgetProps {
  /** mine: the caller's latest projects with their status; gallery: the latest published ones. */
  variant: 'mine' | 'gallery';
}

/** Dashboard widgets « Mes projets » and « Dernières entrées de la galerie » (plan §9.5). */
export const ProjectsWidget: React.FC<ProjectsWidgetProps> = ({ variant }) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    projectService
      .list({ scope: variant === 'mine' ? 'mine' : 'gallery' })
      .then((list) => {
        if (cancelled) return;
        const latest =
          variant === 'gallery'
            ? [...list].sort((a, b) => publishedTime(b) - publishedTime(a))
            : [...list].sort(byLastActivity);
        setProjects(latest.slice(0, LIMIT));
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [variant]);

  const href = variant === 'mine' ? '/projets' : '/galerie';

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-foreground">
          {variant === 'mine' ? t('projects.widgets.mine') : t('projects.widgets.gallery')}
        </h3>
        <Link to={href} className="text-sm text-primary hover:underline">
          {t('projects.widgets.seeAll')}
        </Link>
      </div>
      {projects === null ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('projects.loading')}
        </p>
      ) : projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {variant === 'mine' ? t('projects.widgets.mineEmpty') : t('projects.widgets.galleryEmpty')}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {projects.map((p) => (
            <li key={p.id}>
              <Link to={`/projets/${p.id}`} className="flex items-center gap-3 rounded-md py-2.5 hover:bg-muted/40">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{p.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {[
                      p.domain_name,
                      variant === 'gallery' ? authorName(p) : null,
                      timeAgo(variant === 'gallery' ? p.published_at : lastActivity(p)),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                {variant === 'mine' && <PublishedBadge publishedAt={p.published_at} />}
                <ProjectStatusPill status={p.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
